import { DiffTable } from "@/ui/DiffTable";
import { useEffect, useId, useState } from "react";
import { adminSystem, configApprove, configList, configOpen, configPropose, configReject, type ConfigOpenOutput, type ProposalRow } from "@/api/tools";
import { useRead, useWrite } from "@/api/useTool";
import { businessLocalToISO, isoToBusinessLocal } from "@/api/runtime";
import { Banner, Dialog, Empty, Field, KV, Pager, Pill, ReasonDialog, Receipt, RecordFields, Skeleton, Table, fmtDate, recordLabel } from "@/ui";
import { useSession } from "@/session/useSession";
import { go } from "@/router";

const LOCKED = new Set(["two_2s", "alignment_on_hire", "no_override", "all_five_values", "five_values_covered"]);
const route = (r: ProposalRow) => `admin/config/${encodeURIComponent(r.key)}/v${r.version}`;
/** A version that never took effect is a draft: drafted by its proposer, its shape the draft's (F-42). */
const drafted = (r: Pick<ProposalRow, "status">) => r.status === "draft" || r.status === "rejected";
/** Who acted on a version, labelled by its status: a draft is drafted and awaits approval, a rejected draft names who
 * rejected it, a published version names who published and who approved it. */
function whoActed(r: ProposalRow): [string, string][] {
  const author = r.published_by_name ?? "Not recorded";
  if (r.status === "draft") return [["Drafted by", author], ["Approval", "Awaiting approval"]];
  if (r.status === "rejected") return [["Drafted by", author], ["Rejected by", r.rejected_by_name ?? "Not recorded"]];
  return [["Published by", author], ["Approved by", r.approved_by_name ?? "Not recorded"]];
}
export function AdminConfig({ configKey, version }: { configKey?: string | undefined; version?: string | undefined }) {
  const [pages, setPages] = useState<(string | null)[]>([null]);
  const q = useRead(["config", configKey, pages.at(-1)], () => configList({ key: configKey ?? null, limit: 100, cursor: pages.at(-1) ?? null }));
  const row = q.data?.rows.find((r) => `v${r.version}` === version);
  return (
    <section>
      <h2 className="sec">Configuration versions</h2>
      <p className="muted">A different Admin must approve each draft. Requisitions keep the guide version pinned when their intake was finalized.</p>
      <Banner error={q.error} onRetry={() => void q.refetch()} />
      {q.isLoading && <Skeleton />}
      {configKey && <a href="#admin/config">All configuration keys</a>}
      {q.data && !row && (
        <>
          {!q.data.rows.length ? (
            <Empty heading={configKey ? "No matching configuration" : "No configuration versions yet"} action={configKey ? <a className="btn" href="#admin/config">All configuration keys</a> : undefined}>{configKey ? "No configuration versions match this key." : "Configuration versions appear here when they are published or proposed."}</Empty>
          ) : (
            <Table
              label="Configuration versions"
              head={
                <tr>
                  <th>Key / version</th>
                  <th>Effective from</th>
                  <th>Author / approval</th>
                  <th>Note</th>
                  <th>Status</th>
                </tr>
              }
            >
              {[...q.data.rows].sort((a, b) => Number(["draft", "Scheduled"].includes(b.status) || b.label === "Scheduled") - Number(["draft", "Scheduled"].includes(a.status) || a.label === "Scheduled")).map((r) => (
                <tr key={r.id}>
                  <td>
                    <a href={`#${route(r)}`}>
                      {r.key} · v{r.version}
                    </a>
                  </td>
                  <td>{fmtDate(r.effective_from, true)}</td>
                  <td>
                    {whoActed(r).map(([word, name]) => (
                      <div key={word}>{word === "Approval" ? name : <><span className="muted">{word}</span> {name}</>}</div>
                    ))}
                  </td>
                  <td className="long">{r.note}</td>
                  <td>
                    <Pill tone={r.status === "draft" ? "warn" : "info"}>{r.label}</Pill>
                  </td>
                </tr>
              ))}
            </Table>
          )}
          {version && <Empty heading="Version is outside this page">This version is not on this page.</Empty>}
          <Pager count={q.data.rows.length} page={pages.length}
            previous={pages.length > 1 ? () => setPages(pages.slice(0, -1)) : undefined}
            next={q.data.next_cursor ? () => setPages([...pages, q.data!.next_cursor]) : undefined}
          />
        </>
      )}
      {row && <ConfigVersion key={row.id} row={row} versions={q.data?.rows ?? []} />}
    </section>
  );
}
function ConfigVersion({ row, versions }: { row: ProposalRow; versions: ProposalRow[] }) {
  const { me } = useSession();
  const [against, setAgainst] = useState(versions.find((r) => r.version < row.version)?.id ?? "");
  const q = useRead(["config-open", row.id, against], () => configOpen({ id: row.id, diff_against: against || null }));
  const old = useRead(["config-open", against], () => configOpen({ id: against }), { enabled: !!against });
  const approve = useWrite(configApprove, [["config"], ["config-open"]]);
  const resetApproval = approve.reset;
  useEffect(() => { resetApproval(); }, [resetApproval, row.id, me?.person_id, me?.view_as?.person_id]);
  const approvalBlocked = ["SOD_CONFLICT", "NOT_AUTHOR", "NOT_ROLE", "NOT_SEATED", "VIEW_AS_READ_ONLY"].includes(approve.error?.code ?? "");
  const reject = useWrite(configReject, [["config"], ["config-open"]]);
  const [dialog, setDialog] = useState<"draft" | "reject" | null>(null);
  const clock = useRead(["system"], () => adminSystem({}), { refetchInterval: 15000 });
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, []);
  const r = q.data;
  return (
    <>
      <Banner error={q.error} onRetry={() => void q.refetch()} />
      {q.isLoading && <Skeleton />}
      {r && (
        <>
          <div id="config-approval-refusal"><Banner error={approve.error} /></div>
          <div className="hrow">
            <h2 className="sec">
              {r.key} · v{r.version} <Pill tone={r.status === "draft" ? "warn" : "info"}>{r.label}</Pill>
            </h2>
            <div className="acts">
              <button className="btn" onClick={() => setDialog("draft")}>
                New draft
              </button>
              {r.status === "draft" && (
                <>
                  <button className={`btn${approvalBlocked ? "" : " btn--primary"}`} aria-disabled={approvalBlocked || approve.isPending} aria-describedby={approve.error ? "config-approval-refusal" : undefined} disabled={approvalBlocked || approve.isPending} onClick={() => approve.mutate({ proposal_id: r.id })}>
                    Approve
                  </button>
                  <button className="btn" onClick={() => setDialog("reject")}>
                    Reject
                  </button>
                </>
              )}
            </div>
          </div>
          {approve.isSuccess && <Receipt>Configuration approved · {approve.data.label}</Receipt>}
          <KV
            items={[
              ["Effective from", fmtDate(r.effective_from, true)],
              ...whoActed(r),
              ["Note", r.note],
              ["Rejection reason", r.reject_reason],
            ]}
          />
          {r.label === "Scheduled" && (
            <p role="status">
              Scheduled · takes effect in{" "}
              {Math.max(
                0,
                Math.ceil((Date.parse(r.effective_from) - (clock.data ? Date.parse(clock.data.business_now) + now - clock.dataUpdatedAt : now)) / 3_600_000),
              )}{" "}
              hours. Business clock controls publication.
            </p>
          )}
          {Object.entries(r.pinned_by_version).filter(([, count]) => count > 0).map(([version, count]) => <p key={version}>{count} {count === 1 ? "requisition keeps" : "requisitions keep"} v{version}, pinned when {count === 1 ? "its intake was" : "their intakes were"} finalized.{(r.label === "Draft" || r.label === "Scheduled") && " Finalized requisitions keep their pinned version when this version takes effect."}</p>)}
          <Field label="Diff against" id="config-diff">
            <select id="config-diff" value={against} onChange={(e) => setAgainst(e.target.value)}>
              <option value="">No comparison</option>
              {versions
                .filter((v) => v.id !== r.id)
                .map((v) => (
                  <option value={v.id} key={v.id}>
                    v{v.version} · {v.label}
                  </option>
                ))}
            </select>
          </Field>
          <Banner error={old.error} />
          {against && old.data && <ConfigDiff before={old.data.payload ?? {}} after={r.payload ?? {}} />}
          <h3 className="sec">{drafted(r) ? "Draft shape" : "Published shape"}</h3>
          <ConfigPayload configKey={r.key} payload={r.payload ?? {}} />
          {dialog === "draft" && <Draft source={r} onClose={() => setDialog(null)} />}
          <ReasonDialog
            title="Reject configuration"
            confirm="Reject"
            open={dialog === "reject"}
            onClose={() => setDialog(null)}
            error={reject.error}
            busy={reject.isPending}
            onConfirm={(reason) => reject.mutate({ proposal_id: r.id, reason }, { onSuccess: () => setDialog(null) })}
          />
        </>
      )}
    </>
  );
}
function ConfigPayload({ configKey, payload }: { configKey: string; payload: Record<string, unknown> }) {
  if (configKey.startsWith("guide:"))
    return (
      <div className="admin-guide">
        <h3 className="sec">{String(payload.title ?? configKey)}</h3>
        {Array.isArray(payload.values) &&
          (payload.values as Record<string, unknown>[]).map((v, index) => (
            <section className="card guide" key={String(v.key ?? index)}>
              <h4 className="sec">{String(v.name ?? v.key)}</h4>
              <p>
                <b>Standard assessed.</b> {String(v.assesses ?? "")}
              </p>
              <ul>{Array.isArray(v.questions) && v.questions.map((question, i) => <li key={i}>{String(question)}</li>)}</ul>
              <dl className="anchors">
                {v.anchors != null &&
                  typeof v.anchors === "object" &&
                  Object.entries(v.anchors)
                    .sort(([a], [b]) => Number(b) - Number(a))
                    .map(([score, text]) => (
                      <div key={score}>
                        <dt>{score}</dt>
                        <dd>{String(text)}</dd>
                      </div>
                    ))}
              </dl>
            </section>
          ))}
        <details>
          <summary>Complete guide fields</summary>
          <RecordFields value={payload} />
        </details>
      </div>
    );
  if (configKey === "levels")
    return (
      <Table
        label="Level caps and targets"
        head={
          <tr>
            <th>Level</th>
            <th>Minimum skills</th>
            <th>Onsite cap</th>
            <th>Touchpoints</th>
            <th>Time to Acceptance</th>
          </tr>
        }
      >
        {Object.entries(payload).map(([k, raw]) => {
          const v = raw as Record<string, unknown>;
          return (
            <tr key={k}>
              <td>{String(v.title ?? k)}</td>
              <td>{String(v.min_skills)}</td>
              <td>{String(v.onsite_cap)}</td>
              <td>{String(v.touchpoint_cap)}</td>
              <td>{String(v.ttf_days)} days</td>
            </tr>
          );
        })}
      </Table>
    );
  if (configKey === "rules")
    return (
      <>
        <p className="callout">These are Laws — configuration cannot disable them.</p>
        <Editor value={payload} onChange={() => undefined} path="rules" disabled />
      </>
    );
  return <RecordFields value={payload} />;
}
function flatten(v: unknown, path = ""): Record<string, unknown> {
  if (v !== null && typeof v === "object") return Object.assign({}, ...Object.entries(v).map(([k, x]) => flatten(x, path ? `${path}.${k}` : k)));
  return { [path]: v };
}
/**
 * A changed field's path in words, numbered as the shape numbers it (F-42): a list item is "Item n" from 1, an object's
 * key reads as its label; a guide value's anchors and questions keep their own words.
 */
export function diffPathLabel(path: string, before: Record<string, unknown>, after: Record<string, unknown>): string {
  const match = /^values\.(\d+)\.(anchors|questions)\.(\d+)$/.exec(path);
  if (match) {
    const value = ((after.values ?? before.values) as Record<string, unknown>[] | undefined)?.[Number(match[1])];
    return `${String(value?.name ?? value?.key ?? `Value ${Number(match[1]) + 1}`)} › ${match[2] === "anchors" ? "anchor" : "question"} ${Number(match[3]) + (match[2] === "questions" ? 1 : 0)}`;
  }
  let a: unknown = after, b: unknown = before;
  return path.split(".").map((segment) => {
    const listed = Array.isArray(a) || Array.isArray(b);
    a = a !== null && typeof a === "object" ? (a as Record<string, unknown>)[segment] : undefined;
    b = b !== null && typeof b === "object" ? (b as Record<string, unknown>)[segment] : undefined;
    return listed && /^\d+$/.test(segment) ? `Item ${Number(segment) + 1}` : recordLabel(segment);
  }).join(" › ");
}
function ConfigDiff({ before, after }: { before: Record<string, unknown>; after: Record<string, unknown> }) {
  const a = flatten(before),
    b = flatten(after),
    keys = [...new Set([...Object.keys(a), ...Object.keys(b)])].filter((k) => JSON.stringify(a[k]) !== JSON.stringify(b[k]));
  return keys.length ? <DiffTable label="Configuration diff" rows={keys.map(field => ({ field, label: diffPathLabel(field, before, after), before: a[field], after: b[field] }))} /> : <p className="muted">No changes.</p>;
}
function Draft({ source, onClose }: { source: ConfigOpenOutput; onClose: () => void }) {
  const current = useRead(["config-draft-base", source.key], async () => {
    const versions = await configList({ key: source.key, status: "effective", limit: 100 });
    const base = versions.rows.find((r) => r.label === "Current");
    return base ? configOpen({ id: base.id }) : source;
  });
  const [payload, setPayload] = useState(source.payload ?? {});
  useEffect(() => {
    if (current.data) setPayload(current.data.payload ?? {});
  }, [current.data]);
  const [when, setWhen] = useState(isoToBusinessLocal(new Date(Date.now() + 4 * 86_400_000).toISOString()));
  const [note, setNote] = useState("");
  const [hotfix, setHotfix] = useState(false);
  const [reason, setReason] = useState("");
  const save = useWrite(configPropose, [["config"]]);
  return (
    <Dialog title={`New draft · ${source.key}`} open onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(
            { kind: source.kind, key: source.key, payload, effective_from: businessLocalToISO(when), note, hotfix, hotfix_reason: hotfix ? reason : null },
            {
              onSuccess: (r) => {
                onClose();
                go(route(r));
              },
            },
          );
        }}
      >
        <Banner error={current.error ?? save.error} />
        {current.isLoading && <Skeleton />}
        {source.key === "rules" && <p className="callout">These are Laws — configuration cannot disable them.</p>}
        <Editor value={payload} path={source.key} onChange={(v) => setPayload(v as Record<string, unknown>)} />
        <Field label="Effective from" required id="draft-when" hint="At least one business day ahead unless this is a hotfix. Business time zone.">
          <input id="draft-when" type="datetime-local" required value={when} onChange={(e) => setWhen(e.target.value)} />
        </Field>
        <label className="opt">
          <input type="checkbox" checked={hotfix} onChange={(e) => setHotfix(e.target.checked)} />
          Hotfix
        </label>
        {hotfix && (
          <Field label="Hotfix reason" required id="draft-reason">
            <textarea id="draft-reason" required value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        )}
        <Field label="Note" required id="draft-note">
          <textarea id="draft-note" required value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <p>A different Admin must approve this.</p>
        <div className="acts">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn--primary" disabled={current.isLoading || !!current.error || save.isPending || !note.trim() || (hotfix && !reason.trim())}>
            Propose draft
          </button>
        </div>
      </form>
    </Dialog>
  );
}
/** A per-field editor preserves the stored key's shape and types; it never exposes a raw JSON text area. */
function Editor({ value, onChange, path, disabled = false }: { value: unknown; onChange: (v: unknown) => void; path: string; disabled?: boolean }) {
  const id = useId();
  const label = path.split(".").at(-1)!.replace(/_/g, " ");
  if (Array.isArray(value))
    return (
      <fieldset className="admin-editor">
        <legend>{label}</legend>
        {value.map((v, i) => (
          <div key={i}>
            <Editor value={v} path={`${path}.${i + 1}`} disabled={disabled} onChange={(x) => onChange(value.map((old, n) => (n === i ? x : old)))} />
            {!disabled && (
              <button type="button" className="btn btn--sm" onClick={() => onChange(value.filter((_, n) => n !== i))}>
                Remove {label} {i + 1}
              </button>
            )}
          </div>
        ))}
        {!disabled && typeof value[0] === "string" && (
          <button type="button" className="btn btn--sm" onClick={() => onChange([...value, ""])}>
            Add {label}
          </button>
        )}
      </fieldset>
    );
  if (value !== null && typeof value === "object")
    return (
      <fieldset className="admin-editor">
        <legend>{label}</legend>
        {Object.entries(value).map(([k, v]) => (
          <Editor
            key={k}
            value={v}
            path={`${path}.${k}`}
            disabled={disabled || (path === "rules" && LOCKED.has(k))}
            onChange={(x) => onChange({ ...value, [k]: x })}
          />
        ))}
      </fieldset>
    );
  if (typeof value === "boolean")
    return (
      <label className="opt">
        <input type="checkbox" disabled={disabled} checked={value} onChange={(e) => onChange(e.target.checked)} />
        {label}
        {disabled ? " · locked" : ""}
      </label>
    );
  return (
    <Field label={label} id={id}>
      {typeof value === "number" ? (
        <input id={id} type="number" step="any" disabled={disabled} value={value} onChange={(e) => onChange(e.target.valueAsNumber)} />
      ) : (
        <textarea
          id={id}
          rows={String(value ?? "").length > 100 ? 3 : 1}
          disabled={disabled || value === null}
          value={String(value ?? "")}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </Field>
  );
}
